import { useEffect, useMemo, useState, memo } from 'react';
import { MapContainer, TileLayer, Polygon, CircleMarker, Marker, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { DEFAULT_MAP_STYLE, getMapStyleByKey } from '../constants/mapStyles';
import MapStyleSelector from './MapStyleSelector';
import FireToggle from './FireToggle';
import FireLayer from './FireLayer';

export const CATEGORIES = [
  { key: 'gate',      label: 'Gate / Access', color: '#f59e0b', emoji: '' },
  { key: 'water',     label: 'Water Source',  color: '#3b82f6', emoji: '' },
  { key: 'hazard',    label: 'Hazard',        color: '#ef4444', emoji: ''  },
  { key: 'livestock', label: 'Livestock Pen', color: '#22c55e', emoji: '' },
];
const cat = (key) => CATEGORIES.find(c => c.key === key);

function FitBounds({ positions }) {
  const map = useMap();
  useEffect(() => {
    if (positions.length >= 2) map.fitBounds(positions, { padding: [30, 30] });
  }, [map, positions]);
  return null;
}

function MapEvents({ onChange }) {
  const map = useMap();
  useEffect(() => {
    const handleMove = () => {
      const center = map.getCenter();
      onChange({ zoom: map.getZoom(), center: [center.lat, center.lng] });
    };
    map.on('moveend zoomend', handleMove);
    return () => map.off('moveend zoomend', handleMove);
  }, [map, onChange]);
  return null;
}

const PropertyMap = memo(function PropertyMap({ boundary, features, locations = [], height = 340, zoom, center, onMapChange, mapStyle, onMapStyleChange }) {
  const positions = useMemo(() => (boundary ?? []).map(p => [p.lat, p.lng]), [boundary]);
  const initialCenter = useMemo(() => center ?? [41.5, -99.9], [center]);
  const initialZoom = zoom ?? 7;

  const [internalStyleKey, setInternalStyleKey] = useState(DEFAULT_MAP_STYLE.key);
  const [showFires, setShowFires] = useState(false);
  const isControlled = mapStyle !== undefined;
  const activeStyleKey = isControlled ? mapStyle : internalStyleKey;
  const activeStyle = getMapStyleByKey(activeStyleKey);

  const handleStyleChange = (key) => {
    if (isControlled) {
      onMapStyleChange?.(key);
    } else {
      setInternalStyleKey(key);
    }
  };

  const firesCenter = useMemo(() => {
    if (boundary?.length) {
      const avgLat = boundary.reduce((sum, p) => sum + p.lat, 0) / boundary.length;
      const avgLng = boundary.reduce((sum, p) => sum + p.lng, 0) / boundary.length;
      return { lat: avgLat, lng: avgLng };
    }
    return Array.isArray(initialCenter)
      ? { lat: initialCenter[0], lng: initialCenter[1] }
      : initialCenter;
  }, [boundary, initialCenter]);

  return (
    <div className="property-map detail-map" style={{ position: 'relative', height: `${height}px`, width: '100%' }}>
      <MapStyleSelector active={activeStyleKey} onChange={handleStyleChange} />
      <FireToggle active={showFires} onChange={setShowFires} />
      <MapContainer center={initialCenter} zoom={initialZoom} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true}>
        <TileLayer
          key={activeStyle.key}
          url={activeStyle.url}
          attribution={activeStyle.attribution}
        />
        {onMapChange && <MapEvents onChange={onMapChange} />}
        {positions.length >= 2 && <FitBounds positions={positions} />}
        {positions.length >= 3 && (
          <Polygon positions={positions}
            pathOptions={{ color: '#fe3130', fillColor: '#fe3130', fillOpacity: 0.12, weight: 2 }} />
        )}
        {(features ?? []).map(pin => (
          <CircleMarker key={pin.id} center={[pin.lat, pin.lng]} radius={7}
            pathOptions={{ color: cat(pin.category)?.color, fillColor: cat(pin.category)?.color, fillOpacity: 0.9, weight: 2 }}>
            <Tooltip direction="top" offset={[0, -8]}>
              <strong>{cat(pin.category)?.emoji} {pin.label}</strong>
              {pin.notes && <><br /><span>{pin.notes}</span></>}
            </Tooltip>
          </CircleMarker>
        ))}
        {locations.map(loc => {
          const icon = L.divIcon({
            className: 'responder-pin',
            html: `<div class="responder-pin-dot"></div><div class="responder-pin-label">${loc.name}</div>`,
            iconSize: [80, 38],
            iconAnchor: [40, 30],
          });
          return (
            <Marker key={loc.user_id} position={[loc.lat, loc.lng]} icon={icon}>
              <Tooltip direction="top" offset={[0, -38]}>
                <strong>{loc.name}</strong>
                <br />
                <span>Last seen: {new Date(loc.updated_at).toLocaleTimeString()}</span>
              </Tooltip>
            </Marker>
          );
        })}
        <FireLayer center={firesCenter} visible={showFires} />
      </MapContainer>
    </div>
  );
});

export default PropertyMap;
